export type ManifestState = 'active' | 'unpublished' | 'ended' | 'revoked'

export interface AudienceManifestV1 {
  schemaVersion: 'venue-audience-manifest/v1'
  stadiumId: string
  state: ManifestState
  eventId: string | null
  audienceReleaseUrl: string | null
  audienceReleaseChecksum: string | null
  updatedAt: string
}

export interface ArtifactDescriptor {
  url: string
  sha256: string
  bytes: number
}

export interface AudienceReleaseSkinOptionV1 {
  skinId: string
  name: string
  resolvedSkinId: string
  artifact: ArtifactDescriptor
}

export interface AudienceReleaseSkinPolicyV1 {
  defaultSkinId: string
  allowedSkins: AudienceReleaseSkinOptionV1[]
}

export interface AudienceReleaseV1 {
  schemaVersion: 'venue-audience-release/v1'
  releaseId: string
  stadium: { id: string; name: string; timeZone: string }
  event: { id: string; name: string; startAt: string; endAt: string; status: 'active' | 'ended' }
  versions: { content: string; skin: string; i18n: string }
  artifacts: {
    content: ArtifactDescriptor
    skin: ArtifactDescriptor
    i18n: ArtifactDescriptor
    assets: ArtifactDescriptor
  }
  capabilities: { broadcast: boolean; panorama: boolean }
  /**
   * Backwards-compatible activity skin policy. The legacy `versions.skin` and
   * `artifacts.skin` fields always describe the required default skin. Other
   * allowed skins are optional immutable artifacts and may safely fall back to
   * the default when unavailable.
   */
  skinPolicy?: AudienceReleaseSkinPolicyV1
  publishedAt: string
  offlineValidUntil: string
}

export interface AudienceCapabilities {
  broadcast: boolean
  panorama: boolean
}

export const editableTokenKeys = [
  'brand.primary', 'brand.onPrimary', 'brand.secondary', 'brand.onSecondary',
  'surface.page', 'surface.card', 'surface.elevated', 'surface.scrim',
  'text.primary', 'text.secondary', 'text.inverse',
  'border.subtle', 'border.strong', 'focus.ring',
  'hero.overlayStart', 'hero.overlayEnd',
  'shape.radiusPreset', 'elevation.preset',
] as const

export type EditableTokenKey = (typeof editableTokenKeys)[number]
export type EditableTokens = Record<EditableTokenKey, string>
export type LockedTokenKey =
  | 'status.urgent' | 'status.warning' | 'status.success'
  | 'route.entry' | 'route.exit'
  | 'annotation.medical' | 'annotation.accessible' | 'annotation.service'
  | 'boundary.strong' | 'qr.foreground' | 'qr.background'

export interface PublishedAssetRef {
  assetId: string
  url: string
  mime: 'image/jpeg' | 'image/png' | 'image/webp'
  width: number
  height: number
  sha256: string
}

export interface ResolvedAudienceSkinV1 {
  schemaVersion: 'venue-audience-skin/v1'
  resolvedSkinId: string
  themeResolverVersion: 'vf-theme-resolver/v1'
  sourceVersions: Array<{ scope: 'SYSTEM' | 'STADIUM' | 'EVENT'; versionId: string }>
  tokens: EditableTokens
  lockedTokens: Record<LockedTokenKey, string>
  assets: { logo: PublishedAssetRef | null; hero: PublishedAssetRef | null; pageBackground: PublishedAssetRef | null }
  checksum: string
}

export interface AudienceI18nV1 {
  schemaVersion: 'venue-audience-i18n/v1'
  dictionaryVersion: string
  dictionaryChecksum: string
  ui: Record<string, string>
  content: Array<{ path: string; itemId: string | null; sourceHash: string; value: string; richText?: boolean }>
}

export interface AudienceReleaseContentV1 extends Record<string, unknown> {
  schemaVersion: 'venue-audience-content/v1'
  projectName: string
}

export interface AudienceAssetsV1 extends Record<string, unknown> {
  schemaVersion: 'venue-assets/v1'
  items: Array<Record<string, unknown>>
}

export interface ReleaseContext {
  manifest: AudienceManifestV1
  release: AudienceReleaseV1
  content: AudienceReleaseContentV1
  skin: ResolvedAudienceSkinV1
  i18n: AudienceI18nV1 | null
  assets: AudienceAssetsV1
  verifiedAt: string
}

export type PreviewScene = 'home' | 'entry' | 'route' | 'services' | 'broadcast' | 'annotation' | 'vr'
export type PreviewDisabledCapability = 'network' | 'telemetry' | 'broadcast' | 'externalNavigation' | 'persistentCache'
export interface PreviewReleasePayload {
  previewRevision: number
  content: AudienceReleaseContentV1
  skin: ResolvedAudienceSkinV1
  i18n: AudienceI18nV1
  locale: 'zh-CN' | 'en-US'
  scene: PreviewScene
  disabledCapabilities: PreviewDisabledCapability[]
}
export interface PreviewMessage {
  source: 'venue-flow-ui' | 'venue-h5-audience-next'
  target: 'venue-flow-ui' | 'venue-h5-audience-next'
  protocolVersion: 'venue-audience-preview/v1'
  type: 'READY' | 'APPLY_PREVIEW_RELEASE' | 'SET_LOCALE' | 'SET_SCENE' | 'APPLIED' | 'VALIDATION_ERROR' | 'RUNTIME_ERROR'
  requestId: string
  payload: Record<string, unknown>
}

export type RuntimeSource = 'shared-content' | 'shared-cache' | 'legacy-v44' | 'legacy-cache' | 'preview'
